import { STATUS_WORDS, statusWord, statusTone, scheduleReason } from '../../../components/nucleus/status';
import { plural } from '../../../nucleus/format';

// THE PLUGINS SECTION'S WORDS — one word per idea, everywhere: copy, pills,
// facets, toasts (decision_designs_ledger/2026-10-09_nucleus-plugin-lifecycle.md
// §1). The state words themselves live in components/nucleus/status.js (so a
// StatusPill says them too); everything else a plugin surface says is here.
// Field names are always the field's LABEL, never its key.

const capital = (s) => (s ? `${s[0].toUpperCase()}${s.slice(1)}` : s);
const humanise = (s) => capital(String(s || '').replace(/[_-]+/g, ' ').trim());
/** "a", "a and b", "a, b and c". */
export function listWords(list) {
    const xs = (list || []).filter(Boolean);
    if (xs.length < 2) return xs[0] || '';
    return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

// ── a plugin's lifecycle ─────────────────────────────────────────────────────

/** The lifecycle's states, in the order a plugin moves through them. */
export const PLUGIN_STATES = Object.keys(STATUS_WORDS.plugin);
export const pluginStateWord = (state) => statusWord(state || 'not_installed', 'plugin');
// every step still open, turning it on included, is one state: Setting up
const SETTING_UP = ['not_set_up', 'needs_connection', 'needs_credentials', 'needs_parameters', 'needs_approval'];
/** Whether a lifecycle state is one of Setting up's. */
export const isSettingUp = (state) => SETTING_UP.includes(state);
/** A state as a filter groups it: Setting up, Ready, Off (and Not set up, the store's). */
export const stateGroup = (state) => (isSettingUp(state) ? 'not_set_up' : state);
/** My plugins' State filter, in its order: Setting up · Ready · Off. */
export const MINE_STATES = ['not_set_up', 'ready', 'paused'];

/** A need's fields as their labels: the server sends [{key, label}] (an older one, keys). */
export function fieldLabels(fields) {
    return (fields || []).map((f) => (f && typeof f === 'object' ? f.label || f.key : f)).filter(Boolean);
}
/** A need's fields as their keys, whichever shape they came in. */
export function fieldKeys(fields) {
    return (fields || []).map((f) => (f && typeof f === 'object' ? f.key : f)).filter(Boolean);
}

/** What one need asks for, in a few words, its fields by label. */
// WHAT TO DO NEXT, AS AN INSTRUCTION: a verb, then the thing by its own name —
// the service to sign in to, the field to fill in, what changed. A person
// should never have to know what "credentials" or "parameters" mean here.

/** The first thing a changed plugin now does that it didn't: "create health-metric". */
export function newInChange(approval) {
    const added = (approval?.changes || []).flatMap((c) => c?.added || []).filter(Boolean);
    return added[0] || null;
}

/** What one need asks you to do, as an instruction: "Sign in to Google Calendar",
 *  "Paste your Readwise access token", "Fill in Time zone and Ready by".
 *  `approval` (the lifecycle's) names what changed when it must be approved again. */
export function needWord(n, approval = null) {
    if (!n) return '';
    const named = listWords(fieldLabels(n.fields));
    switch (n.kind) {
        case 'install': return 'Set it up';
        case 'paused': return 'Turn it on';
        case 'connection': {
            const where = n.provider || 'the service it uses';
            return n.reason === 'revoked' ? `Sign in to ${where} again` : `Sign in to ${where}`;
        }
        case 'credentials': return named ? `Paste your ${named}` : 'Paste the key it needs';
        case 'parameters': return named ? `Fill in ${named}` : 'Fill in its settings';
        case 'approval': {
            if (n.reason !== 'changed') return 'Turn it on';
            const added = newInChange(approval);
            return added ? `Review what changed — new: ${added}` : 'Review what changed';
        }
        case 'realm': return 'Choose a realm it runs in';
        default: return humanise(n.kind);
    }
}

/** A tool stuck running automatically, said with its reason: "Search pages can't run: its realm does not let it create".
 *  `named: false` leaves the tool's name off, for its own row: "Can't run: …", "Keeps failing: …". */
export function stuckWord(s, { named = true } = {}) {
    // a failing one says its last error when nothing else says why
    const why = scheduleWhy(s) || (s?.state === 'failing' && s?.last_error ? plainError(s.last_error) : null);
    const tool = named ? s?.tool || 'It' : null;
    const said = (verb) => (tool ? `${tool} ${verb}` : capital(verb));
    if (s?.state === 'failing') return why ? `${said('keeps failing')}: ${lower(why)}` : said('keeps failing');
    return why ? `${said('can’t run')}: ${lower(why)}` : said('can’t run');
}

/** How a tool runs, for its row: "When you run it", or how it runs
 *  automatically ("Every hour", "Every day at 7:00"). Switched off, it runs only when you run it. */
export function howItRuns(s) {
    const state = s?.state || 'off';
    const freq = s?.frequency || s?.cron || null;
    if (state === 'off' || !freq) return 'When you run it';
    const preset = FREQUENCIES.find((f) => f.id === freq || f.cron === String(freq).trim());
    return (preset ? preset.label : describeCron(freq)) || 'Runs automatically';
}
/** Turning a plugin Off asks first, in these words. */
export const turnOffAsk = (name) => ({
    title: `Turn off ${name}?`,
    body: 'No tool runs automatically or by an automation, and no app or agent can use it. You can still press Run yourself.',
    label: 'Turn off',
    danger: false,
});

/** "Next: Sign in to Google Calendar" — the one thing Setting up waits on. */
export const nextWord = (step) => (step ? `Next: ${step}` : null);
// a sentence's first letter lowered, unless the word is an acronym ("HTTP")
function lower(s) { return s ? `${/^[A-Z]{2}/.test(s) ? s[0] : s[0].toLowerCase()}${s.slice(1)}`.replace(/\.$/, '') : s; }
// an error as words: no exception class in front ("HTTPError: 500 Server Error" → "500 Server Error")
const plainError = (e) => String(e).replace(/^(?:[A-Z][a-z]*[A-Z][A-Za-z]*|[A-Z][A-Za-z]*(?:Error|Exception)):\s*/, '').trim() || String(e);

// the setup's steps, as the set-up sheet and the inspector's checklist name them
export const SETUP_STEPS = [
    { id: 'account', title: 'Sign in', need: 'connection' },
    { id: 'credentials', title: 'Keys', need: 'credentials' },
    { id: 'settings', title: 'Settings', need: 'parameters' },
    { id: 'approve', title: 'Turn on', need: 'approval' },
];
/** A checklist row's end: ✓ Done, Missing, Not needed. */
export const CHECK_WORD = { done: 'Done', missing: 'Missing', not_needed: 'Not needed' };

/** Verified by ofSelf / Not verified. */
export const verifiedWord = (v) => (v ? 'Verified by ofSelf' : 'Not verified');
/** What a verified (or not) plugin means for apps and agents. */
export const verifiedMeans = (v) => (v ? 'Apps and agents can use it' : 'Apps and agents can’t use it until it’s verified');

// ── runs automatically (a tool's one timetable; the server's `schedule`) ─────

/** Keeps failing · Can't run · On · Off, by the server's states, in the order they want a hand. */
export const SCHEDULE_STATES = ['failing', 'paused', 'needs_realm', 'running', 'off'];
export const scheduleWord = (state) => statusWord(state || 'off', 'schedule');

// why it can't run, in a few words, for a row's end ("Can't run: plugin not turned on")
const SHORT_REASON = {
    not_approved: 'plugin not turned on',
    per_run_consent: 'it asks you every run',
    not_connected: 'not signed in',
    not_configured: 'a setting is empty',
    realm_revoked: 'its realm is gone',
    realm_cannot_write: 'its realm can’t write',
    needs_realm: 'choose a realm it runs in',
    tool_missing: 'its tool is gone',
    plugin_paused: 'plugin off',
};
export const shortReason = (reason) => (reason ? SHORT_REASON[reason] || String(reason).replace(/_/g, ' ') : null);

/** Why it can't run automatically, or is off, in a sentence: the scheduler's own
 *  words when it switched it off (`disabled_text`), else its reason's. */
export function scheduleWhy(s) {
    if (!s) return null;
    if (s.disabled_text) return s.disabled_text;
    if (s.state === 'needs_realm' && !s.reason) return scheduleReason('needs_realm');
    return scheduleReason(s.reason, s.cannot_write);
}

/** Who set a tool running automatically, when an app did: "Made by Strava Sync". */
export const scheduleMadeBy = (s) => (s?.made_by_app?.name ? `Made by ${s.made_by_app.name}` : null);

/** How a tool runs automatically, in a line: "Every day at 7:00", "Can't run: plugin
 *  not turned on", "Keeps failing · Every hour", "Off", or "When you run it". */
export function scheduleLine(s) {
    const state = s?.state || 'off';
    const when = s?.frequency || s?.cron ? frequencyWord(s.frequency || s.cron) : null;
    if (state === 'running') return when || scheduleWord('running');
    if (state === 'failing') return when ? `Keeps failing · ${when}` : scheduleWord('failing');
    if (state === 'paused' || state === 'needs_realm') {
        const why = shortReason(s?.reason || (state === 'needs_realm' ? 'needs_realm' : null));
        return why ? `${scheduleWord(state)}: ${why}` : scheduleWord(state);
    }
    // Off with no frequency is a tool that has never run automatically.
    if (state === 'off' && !when) return 'When you run it';
    return scheduleWord(state);
}

/** The act that lets it run again: { id, label, danger? } or null. */
const FIX = {
    not_approved: { id: 'approve', label: 'Turn on' },
    per_run_consent: { id: 'stop_asking', label: 'Stop asking every run' },
    not_connected: { id: 'connect', label: 'Sign in' },
    not_configured: { id: 'settings', label: 'Settings' },
    realm_revoked: { id: 'realm', label: 'Choose a realm' },
    needs_realm: { id: 'realm', label: 'Choose a realm' },
    realm_cannot_write: { id: 'open_realm', label: 'Open realm' },
    tool_missing: { id: 'delete_schedule', label: 'Delete', danger: true },
    plugin_paused: { id: 'resume', label: 'Turn on' },
};
export function scheduleFix(s) {
    const reason = s?.reason || (s?.state === 'needs_realm' ? 'needs_realm' : null);
    return reason ? FIX[reason] || null : null;
}

/** A plugin's tools that run automatically, counted by state: "2 on · 1 can't run". */
export function schedulesLine(schedules) {
    const n = (...st) => (schedules || []).filter((s) => st.includes(s?.state)).length;
    return [
        n('running') && `${n('running')} on`,
        n('failing') && `${n('failing')} ${n('failing') === 1 ? 'keeps' : 'keep'} failing`,
        n('paused', 'needs_realm') && `${n('paused', 'needs_realm')} can’t run`,
    ].filter(Boolean).join(' · ');
}

// ── frequencies ──────────────────────────────────────────────────────────────

/** The four the setup offers, by the server's word and its cron. */
export const FREQUENCIES = [
    { id: 'hourly', cron: '0 * * * *', label: 'Every hour' },
    { id: 'six-hourly', cron: '0 */6 * * *', label: 'Every 6 hours' },
    { id: 'daily', cron: '0 7 * * *', label: 'Every day at 7:00' },
    { id: 'weekly', cron: '0 7 * * 1', label: 'Every Monday at 7:00' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_NAMES = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const DAY_NAMES = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const NAMED = { '@hourly': '0 * * * *', '@daily': '0 0 * * *', '@midnight': '0 0 * * *', '@weekly': '0 0 * * 0', '@monthly': '0 0 1 * *', '@yearly': '0 0 1 1 *', '@annually': '0 0 1 1 *' };

const isInt = (s) => /^\d+$/.test(s);
const ordinal = (n) => {
    const t = n % 100;
    if (t >= 11 && t <= 13) return `${n}th`;
    return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
};
const clock = (h, m) => `${h}:${String(m).padStart(2, '0')}`;

// a cron field as a sorted list of numbers (a list, ranges, names), or null for one this reads not
function numbers(field, lo, hi, names = {}) {
    const out = new Set();
    for (const part of field.split(',')) {
        const p = part.toLowerCase();
        const range = p.match(/^([a-z0-9]+)-([a-z0-9]+)$/);
        const one = (x) => (isInt(x) ? Number(x) : names[x] ?? null);
        if (range) {
            const a = one(range[1]); const b = one(range[2]);
            if (a == null || b == null || a > b) return null;
            for (let i = a; i <= b; i += 1) out.add(i);
        } else {
            const v = one(p);
            if (v == null) return null;
            out.add(v);
        }
    }
    const list = [...out].sort((a, b) => a - b);
    return list.every((v) => v >= lo && v <= hi) ? list : null;
}

function daysWord(dows) {
    const d = [...new Set(dows.map((x) => (x === 7 ? 0 : x)))].sort((a, b) => a - b);
    const key = d.join(',');
    if (key === '1,2,3,4,5') return 'Every weekday';
    if (key === '0,6') return 'Every Saturday and Sunday';
    if (d.length === 7) return 'Every day';
    // the week from Monday, as people read it
    const fromMonday = [...d.filter((x) => x !== 0), ...d.filter((x) => x === 0)];
    return `Every ${listWords(fromMonday.map((x) => DAYS[x]))}`;
}

/** A plain reading of a cron's common forms ("Every weekday at 8:30"), or null when it says something rarer. */
export function describeCron(cron) {
    const raw = String(cron || '').trim();
    const parts = (NAMED[raw.toLowerCase()] || raw).split(/\s+/);
    if (parts.length !== 5) return null;
    const [mi, hr, dom, mon, dow] = parts;
    const everyDay = dom === '*' && mon === '*' && (dow === '*' || dow === '?');
    const dayAny = dom === '*' || dom === '?';

    // minutes and hours, on every day
    if (everyDay) {
        if (mi === '*' && hr === '*') return 'Every minute';
        const stepM = mi.match(/^(?:\*|0)\/(\d+)$/);
        if (stepM && hr === '*') return Number(stepM[1]) === 1 ? 'Every minute' : `Every ${stepM[1]} minutes`;
        if (isInt(mi) && hr === '*') return Number(mi) === 0 ? 'Every hour' : `Every hour at :${mi.padStart(2, '0')}`;
        const stepH = hr.match(/^(?:\*|0)\/(\d+)$/);
        if (isInt(mi) && stepH) {
            const n = Number(stepH[1]);
            const every = n === 1 ? 'Every hour' : `Every ${n} hours`;
            return Number(mi) === 0 ? every : `${every} at :${mi.padStart(2, '0')}`;
        }
    }
    // the rest name a time of day
    if (!isInt(mi)) return null;
    const hours = numbers(hr, 0, 23);
    if (!hours || hours.length > 4) return null;
    const at = `at ${listWords(hours.map((h) => clock(h, Number(mi))))}`;
    if (everyDay) return `Every day ${at}`;
    if (dayAny && mon === '*') {
        const dows = numbers(dow, 0, 7, DAY_NAMES);
        return dows ? `${daysWord(dows)} ${at}` : null;
    }
    if (dow !== '*' && dow !== '?') return null;
    const days = numbers(dom, 1, 31);
    if (!days || days.length > 3) return null;
    const on = listWords(days.map(ordinal));
    if (mon === '*') return `Every month on the ${on} ${at}`;
    const months = numbers(mon, 1, 12, MONTH_NAMES);
    if (!months || months.length !== 1 || days.length !== 1) return null;
    return `Every year on ${days[0]} ${MONTHS[months[0] - 1]} ${at}`;
}

/** How often, in words that state the time: a preset by its word or its cron,
 *  any other cron as "Custom · ‹its plain reading›" (or "Custom · ‹cron›"). */
export function frequencyWord(freq) {
    if (!freq) return null;
    const preset = FREQUENCIES.find((f) => f.id === freq || f.cron === String(freq).trim());
    if (preset) return preset.label;
    const read = describeCron(freq);
    return `Custom · ${read || String(freq).trim()}`;
}

// ── automations ──────────────────────────────────────────────────────────────

/** Runs automatically · When something changes · When you run it. */
export const TRIGGER_WORD = { schedule: 'Runs automatically', event: 'When something changes', manual: 'When you run it' };
export const triggerKindWord = (kind) => TRIGGER_WORD[kind] || TRIGGER_WORD.manual;
/** Keeps failing · On · Off, in the order they want a hand. */
export const AUTOMATION_STATES = ['failing', 'on', 'off'];
export const automationWord = (state) => statusWord(state, 'automation');

// ── runs ─────────────────────────────────────────────────────────────────────

/** Every outcome a run can have (and Running, while it runs). */
export const OUTCOMES = ['done', 'partial', 'failed', 'refused', 'waiting', 'skipped', 'cancelled', 'unknown'];
export const outcomeWord = (outcome) => statusWord(outcome || 'unknown', 'run');
export const outcomeTone = (outcome) => statusTone(outcome || 'unknown');
/** A run dot's tone: ok, running, warn, quiet or failed. */
export function dotTone(outcome) {
    if (outcome === 'done') return 'ok';
    if (outcome === 'running') return 'running';
    if (outcome === 'partial' || outcome === 'waiting') return 'warn';
    if (outcome === 'failed' || outcome === 'refused') return 'failed';
    return 'quiet';
}

// the stages a run is refused at before the plugin is called (runner.BEFORE_THE_CALL)
const BEFORE_THE_CALL = ['reach', 'tool', 'install', 'approval', 'parameters', 'grant', 'inputs', 'remove_source', 'consent', 'paused'];

/** A run's outcome: the server's (`outcome`), else read from an older row's status. */
export function outcomeOf(row) {
    const given = row?.outcome;
    if (given && (OUTCOMES.includes(given) || given === 'running')) return given;
    const status = row?.status || 'unknown';
    const sum = row?.output_summary || row?.summary || {};
    if (status === 'failed' || status === 'error') {
        if (sum.refused_at && BEFORE_THE_CALL.includes(sum.refused_at)) return 'refused';
        if (sum.code === 'OUTCOME_UNKNOWN' || sum.outcome_unknown) return 'unknown';
        return 'failed';
    }
    if (status === 'success' || status === 'completed') {
        const dropped = Object.values(sum.outputs_dropped || {}).some((c) => c > 0);
        return dropped || (sum.cannot_write || []).length ? 'partial' : 'done';
    }
    if (status === 'running') return 'running';
    if (['pending', 'queued', 'dispatched', 'parked', 'waiting'].includes(status)) return 'waiting';
    if (status === 'skipped' || status === 'cancelled') return status;
    return 'unknown';
}

// WHERE A RUN WAS REFUSED, plainly
const STAGE_WORD = {
    reach: 'it isn’t one you can reach here',
    tool: 'that tool isn’t there',
    install: 'it isn’t set up here',
    approval: 'it isn’t turned on',
    parameters: 'a setting it needs has no value',
    grant: 'its realm doesn’t let it run',
    inputs: 'an input it needs has no value',
    remove_source: 'it may not delete what it read',
    consent: 'it needed a yes nobody gave',
    paused: 'the plugin is off',
    call: 'its developer’s server said no',
};
const CODE_STAGE = { PLUGIN_PAUSED: 'paused', NOT_INSTALLED: 'install', NOT_CONFIGURED: 'parameters', CONSENT_REQUIRED: 'consent', TOOL_ID_REQUIRED: 'tool' };
/** "Refused: it isn't approved", from a run's summary ({ refused_at, code }). */
export function refusedLine(summary) {
    const stage = CODE_STAGE[summary?.code] || summary?.refused_at;
    if (!stage) return null;
    return `Refused: ${STAGE_WORD[stage] || String(stage).replace(/_/g, ' ')}`;
}
export const stageWord = (stage) => (stage ? STAGE_WORD[stage] || String(stage).replace(/_/g, ' ') : null);

// WHO STARTED A RUN, named (plugin-words.md, Started by)
export const STARTED_BY_KINDS = [
    { id: 'person', label: 'People' },
    { id: 'schedule', label: 'Runs automatically' },
    { id: 'event', label: 'Automations' },
    { id: 'flow', label: 'Flows' },
    { id: 'app', label: 'Apps' },
    { id: 'agent', label: 'Agents' },
    { id: 'push', label: 'Plugins' },
    { id: 'replay', label: 'Allowed by you' },
    { id: 'test', label: 'Test runs' },
];
// an older row's trigger_source, as the kind it is
const SOURCE_KIND = {
    manual: 'person', press: 'person', canvas: 'person', schedule: 'schedule', event: 'event', automation: 'event', flow: 'flow',
    app: 'app', agent: 'agent', push: 'push', replay: 'replay', test: 'test', dry_run: 'test',
};
export const startedByKind = (row) => row?.started_by?.kind || SOURCE_KIND[row?.trigger_source] || null;
/** "Planner (agent of Day)", or the agent alone when its app isn't named. */
export const agentWord = (agent, app) => (app ? `${agent} (agent of ${app})` : agent);

/** Who started it: You · a member · Runs automatically · the automation · the
 *  flow · the app · "Planner (agent of Day)" · the plugin (its service sent data
 *  in) · Test run, and "You allowed it" for a run that waited for your Allow.
 *  The server's `started_by.name` when it sent one, else made from its parts
 *  (or an older row's trigger_source). `ctx`: { pluginName } for a push the
 *  row does not name. */
export function startedByWord(row, ctx = {}) {
    const by = row?.started_by || null;
    const kind = startedByKind(row);
    // said here, whatever the server's own words: a timetable, and an agent by its parts
    if (kind === 'schedule') return 'Runs automatically';
    if (kind === 'agent' && by?.agent_name) return agentWord(by.agent_name, by.app_name);
    // else the server says it whole ("You", "Morning")
    if (by?.name) return by.name;
    const name = by?.name || null;
    switch (kind) {
        case 'person': return by?.you || !(by?.person_name || name) ? 'You' : by.person_name || name;
        case 'app': return by?.app_name || name || 'Unnamed app';
        case 'agent': return agentWord(by?.agent_name || name || 'Unnamed agent', by?.app_name);
        case 'schedule': return 'Runs automatically';
        case 'event': return by?.automation_name || row?.automation_name || ctx.automationName || 'An automation';
        case 'flow': return by?.flow_name || 'A flow';
        case 'push': return by?.plugin_name || ctx.pluginName || row?.plugin_name || 'Its plugin';
        case 'replay': return 'You allowed it';
        case 'test': return 'Test run';
        default: return null;
    }
}

// WHY A RUN ASKED (a parked run's consent_reason), plainly
const CONSENT_WORD = {
    subject_required: 'You asked to be asked every run',
    tool_requires_ask: 'This tool is set to ask you',
    developer_opted_in: 'Its developer asks for a yes each run',
    destructive_action: 'It does something that can’t be undone',
    removes_source: 'It deletes what it reads',
    setup_not_consented: 'It isn’t turned on yet',
    first_use: 'The first time this app uses it',
    consent_required: 'It needs your yes',
};
/** The server's own words (`reason_text`) when it sent them, else ours. */
export const consentWord = (reason, reasonText = null) => reasonText || CONSENT_WORD[reason] || (reason ? humanise(reason) : 'It needs your yes');

// WHAT A RUN LEFT OUT (receipt.outputs_dropped), by why: what it made but
// couldn't keep, because its realm or the tool's approval doesn't allow it
export const LEFT_OUT_WORD = {
    outside_grant: 'Its realm doesn’t allow it',
    reserved_schema: 'Of a schema only ofSelf writes',
    invalid_reference: 'Pointing at a node it may not use',
    not_declared: 'Not something the tool asks to write',
    not_an_input: 'On a node it was not given',
    not_found: 'On a node that is not there',
    malformed: 'Not in a shape ofSelf reads',
    invalid: 'Not a valid value',
    nothing_to_propose: 'Nothing to propose',
    already_proposed: 'Already proposed',
};
/** What a run left out, by why: [{ why, word, count }], the most first. */
export function leftOutOf(outputsDropped) {
    return Object.entries(outputsDropped || {})
        .filter(([, c]) => c > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([why, count]) => ({ why, count, word: LEFT_OUT_WORD[why] || humanise(why) }));
}
/** "Left out · Its realm doesn't allow it". */
export const leftOutLine = (entry) => `Left out · ${entry.word}`;

// ── Tool access: what an app or agent may do with a tool ────────────────────

export const ACCESS_MODES = ['allow', 'ask', 'deny'];
export const ACCESS_WORD = { allow: 'Allowed', ask: 'Asks you', deny: 'Never' };
/** Allowed · Asks you · Never; a tool you haven't set is Never. */
export const accessWord = (mode) => ACCESS_WORD[mode] || ACCESS_WORD.deny;
/** An app's tools summed up: "2 tools allowed · 1 asks you", or "No tools". */
export function accessSummary(tools) {
    const list = tools || [];
    const n = (m) => list.filter((t) => (t.mode || 'deny') === m).length;
    const parts = [
        n('allow') && `${plural(n('allow'), 'tool')} allowed`,
        n('ask') && `${n('ask')} ${n('ask') === 1 ? 'asks' : 'ask'} you`,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'No tools';
}

// the kinds of node a tool reads or writes, by name. The server sends words
// ("message", "create message", a bare "edit": the nodes it is given) or
// { schema_name | name, action? } (GET /tools); an id is never said
const WRITE_VERBS = /^(create|edit|update|delete|propose|link|relate)\b\s*/i;
const kindsOf = (list, { writes = false } = {}) => [...new Set((Array.isArray(list) ? list : []).map((x) => {
    if (x && typeof x === 'object') return x.schema_name || x.name || (writes && x.action ? 'nodes it is given' : null);
    if (typeof x !== 'string') return null;
    if (!writes) return x;
    const kind = x.replace(WRITE_VERBS, '').trim();
    return kind || 'nodes it is given';
}).filter((x) => x && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(x)).map((x) => String(x).replace(/[_]+/g, ' ')))];
/** What a tool asks for, in a line: "Reads message · Writes message · Can't be undone".
 *  A part the server didn't send is not said; null when nothing is. */
export function toolAsksLine({ reads, writes, irreversible } = {}) {
    const r = kindsOf(reads);
    const w = kindsOf(writes, { writes: true });
    return [
        r.length && `Reads ${listWords(r)}`,
        w.length && `Writes ${listWords(w)}`,
        irreversible && 'Can’t be undone',
    ].filter(Boolean).join(' · ') || null;
}

// ── remove: what goes, what stays (GET …/install/preview) ───────────────────

/** { goes: [line], stays: [line] }; counts the server did not send are not said. */
export function uninstallWords(p) {
    const has = (k) => typeof p?.[k] === 'number' && p[k] > 0;
    const yes = (k) => p?.[k] === true || has(k);
    return {
        goes: [
            (yes('setup') || yes('approval')) && 'Its setup',
            yes('credentials') && 'Its keys',
            yes('connection') && 'Its sign-in',
            has('schedules') && (p.schedules === 1 ? 'How its tool runs automatically' : `How its ${p.schedules} tools run automatically`),
            has('automations') && plural(p.automations, 'automation'),
            has('flows') && `Its place in ${plural(p.flows, 'flow')}`,
            has('requests') && `${plural(p.requests, 'run')} waiting for you (cancelled)`,
            has('proposals') && `${plural(p.proposals, 'proposal')} waiting in Review (can no longer be applied)`,
            (has('access') || has('grants')) && 'Every app’s access to its tools',
        ].filter(Boolean),
        stays: [
            has('runs') && `Its ${plural(p.runs, 'run')}`,
            has('nodes') && `The ${plural(p.nodes, 'node')} it wrote`,
        ].filter(Boolean),
    };
}
